// Pure line reconstruction for pdf.js text items — no pdf.js import, so it is unit-testable in Jest.
// See pdfText.js for why text is rebuilt into lines (and why column gaps become two spaces).

// Gap thresholds, as a multiple of the neighbouring items' average CHARACTER width (not the font height:
// a word space is ~0.25 em in a proportional font but ~0.6 em in a monospace one, whereas it is always about
// one character wide or less). A gap wider than ~1.8 characters is a table-column gap.
const COLUMN_GAP_CHARS = 1.8;
const WORD_GAP_CHARS = 0.15;

const charWidth = (c) => (c.w > 0 ? c.w / Math.max(1, c.s.length) : c.h * 0.5);

/**
 * Pure: converts pdf.js text items (one page) to text, one line per visual row.
 * Items are `{ str, transform: [a, b, c, d, x, y], width, height }`.
 */
export const itemsToText = (items) => {
  const cells = [];
  for (const it of items || []) {
    if (typeof it.str !== 'string' || it.str.trim() === '' || !Array.isArray(it.transform)) continue;
    cells.push({
      s: it.str.trim(),
      x: it.transform[4],
      y: it.transform[5],
      w: it.width || 0,
      h: Math.abs(it.height || it.transform[3]) || 10,
    });
  }
  if (!cells.length) return '';

  // Cluster by baseline first, THEN order each cluster left-to-right: sorting by y alone would shuffle
  // items whose baselines differ by a fraction of a point (superscripts, rounding).
  cells.sort((a, b) => b.y - a.y);
  const rows = [];
  for (const c of cells) {
    const row = rows[rows.length - 1];
    if (row && Math.abs(c.y - row.y) <= Math.max(2, 0.5 * Math.min(c.h, row.h))) {
      row.items.push(c);
      row.y = (row.y * (row.items.length - 1) + c.y) / row.items.length;
    } else {
      rows.push({ y: c.y, h: c.h, items: [c] });
    }
  }

  return rows.map(({ items }) => {
    items.sort((a, b) => a.x - b.x);
    let line = items[0].s;
    for (let i = 1; i < items.length; i += 1) {
      const prev = items[i - 1];
      const cur = items[i];
      const gap = cur.x - (prev.x + prev.w);
      const charW = (charWidth(prev) + charWidth(cur)) / 2;
      line += (gap > COLUMN_GAP_CHARS * charW ? '  ' : gap > WORD_GAP_CHARS * charW ? ' ' : '') + cur.s;
    }
    return line;
  }).join('\n');
};
